import { DrawingService2020 } from '../DrawingService2020.js';
import { HtmlUpdateMaterialImageVisitor, HtmlLabelVisitor, HtmlRenderVisitor } from './HtmlRenderVisitor.js';
import { ToggleInteractivityVisitor } from '../ProductVisitors.js';

/**
 * The HtmlDrawingService implements the interface referenced by the Component/Product classes for
 * updating the drawing (in this case a html/css based 2D drawing).
 */
export class HtmlDrawingService extends DrawingService2020 {
  constructor(productDrawing, loadedMaterials, getProductFinishName, onLoadSingleMaterial) {
    super(productDrawing, loadedMaterials, getProductFinishName, onLoadSingleMaterial);
  }

  /**
   * Return true if we want the door front object to add the doors in odd index position first.
   * Return false if we want to add the doors in the original sequential order.
   */
  addOddDoorsFirst() {
    return false;
  }

  //#region DisplayObject Methods

  performInlineUpdate(component, onInitVisitor) {
    if (this.isDrawingUpdatesEnabled) {
      const visitor = new HtmlRenderVisitor(this.loadedMaterials, this.productDrawing);
      visitor.isInlineUpdate = true;
      this.assignMaterialJitLoaderTo(visitor);
      if (onInitVisitor) {
        onInitVisitor(visitor);
      }
      visitor.visit(component);
    }
  }

  /** E.g. Called for each panel and divider after the door core has been reconfigured. */
  setDisplayObjectPosition(component) {
    this.performInlineUpdate(component);
  }

  refreshMaterialImages(component) {
    if (this.isDrawingUpdatesEnabled) {
      const visitor = new HtmlUpdateMaterialImageVisitor();
      visitor.visit(component);
    }
  }

  /** Creates a new html element (depending on the component type and material). */
  createDisplayObject(component) {
    if (this.isDrawingUpdatesEnabled) {
      const visitor = new HtmlRenderVisitor(this.loadedMaterials, this.productDrawing);
      this.assignMaterialJitLoaderTo(visitor);
      return visitor.visit(component);
    }
  }

  /** Creates a new html element (depending on the component type and material). */
  createMaterialObject(component) {
    return this.createDisplayObject(component);
  }

  /** Creates a new html element (depending on the component type and material). */
  createContainer(component) {
    return this.createDisplayObject(component);
  }

  /**
   * Adds the display object as a child of the parent display object.
   */
  addToParentDisplayObject(parent, displayObject) {
    if (this.isDrawingUpdatesEnabled && parent && displayObject) {
      parent.appendChild(displayObject);
    }
  }

  /**
   * Adds the component's display object as a child of the parent component's display object.
   */
  addChildDisplayObject(component) {
    if (component.parent) {
      this.addToParentDisplayObject(component.parent.displayObject, component.displayObject);
    }
  }

  removeChildDisplayObject(displayObject) {
    if (this.isDrawingUpdatesEnabled) {
      if (displayObject.parentElement) {
        displayObject.parentElement.removeChild(displayObject);
      }
      // displayObject.destroy();
    }
  }

  /**
   * Replaces an old display object with a new one in the parent's child list.
   */
  replaceDisplayObject(oldChild, newChild) {
    if (this.isDrawingUpdatesEnabled && oldChild && oldChild.parentElement) {
      if (newChild) {
        oldChild.parentElement.replaceChild(newChild, oldChild); // Yes, new before old!
      }
      oldChild.parentElement.removeChild(oldChild);
    }
  }

  //#endregion DisplayObject Methods

  /**
   * Currently only used in the simulator app. The code here is similar to the code in
   * HtmlDrawingEngine.toggleDisplayObjectInteractive.
   */
  toggleInteractivity(rootComponent, on, clickHandler) {
    // The visitor is responsible for adding/removing interactivity on appropriate product components.
    const visitor = new ToggleInteractivityVisitor(component => {
      const { displayObject } = component;
      if (displayObject) {
        displayObject.style.cursor = on ? 'pointer' : '';
        if (on) {
          displayObject.addEventListener('click', clickHandler);
        } else {
          displayObject.removeEventListener('click', clickHandler);
        }
      }
    });
    visitor.visitRoot(rootComponent);
  }

  doorLayoutWasReplaced(door) {
    this.performInlineUpdate(door.core, visitor => {
      visitor.isReplacingDoorCore = true;
      visitor.clearChildren(door.core.displayObject);
    });
  }

  //#region Component Labels

  toggleFinishLabels(component, on) {
    const visitor = new HtmlLabelVisitor(on, this.getProductFinishName);
    visitor.visitRoot(component);
  }

  updateFinishLabel(component) {
    if (HtmlLabelVisitor.isLabelVisible(component)) {
      const visitor = new HtmlLabelVisitor(true, this.getProductFinishName);
      visitor.visitRoot(component);
    }
  }

  /**
   * Perform necessary clean up to help the browser's memory management and to avoid leaks.
   */
  detachFinishLabel(component) {
    component.finishLabel = null;
  }

  //#endregion Component Labels

}
